/**
 * Demo clinic-day simulation.
 *
 * Drives the app like a live clinic: every tick it advances the queue
 * (calls the next serial, completes the current patient), registers walk-ins
 * and raises notifications, so dashboards, queues and the bell all move on
 * their own — useful for demos and for testing realtime behaviour.
 *
 * Everything it writes is real data for the signed-in doctor, so realtime
 * subscriptions and query invalidation behave exactly as in production.
 */

import { supabase } from "@/integrations/supabase/client";

export type SimEvent = {
  id: string;
  at: number;
  label: string;
  detail: string;
};

type Listener = (state: SimState) => void;

export type SimState = {
  running: boolean;
  intervalMs: number;
  ticks: number;
  events: SimEvent[];
  lastError: string | null;
};

const KEY_RUNNING = "doctorsx.demoSim.running";
const KEY_SPEED = "doctorsx.demoSim.speed";
const MAX_EVENTS = 25;

const readBool = (k: string, fallback: boolean) => {
  if (typeof window === "undefined") return fallback;
  const raw = window.localStorage.getItem(k);
  return raw === null ? fallback : raw === "true";
};

const readNum = (k: string, fallback: number) => {
  if (typeof window === "undefined") return fallback;
  const raw = Number(window.localStorage.getItem(k));
  return Number.isFinite(raw) && raw > 0 ? raw : fallback;
};

let state: SimState = {
  running: false,
  intervalMs: readNum(KEY_SPEED, 20000),
  ticks: 0,
  events: [],
  lastError: null,
};

const listeners = new Set<Listener>();
let timer: ReturnType<typeof setInterval> | null = null;
let userId: string | null = null;
let onChange: (() => void) | null = null;
let busy = false;

const emit = () => {
  state = { ...state };
  listeners.forEach((fn) => fn(state));
};

const log = (label: string, detail: string) => {
  state.events = [
    { id: crypto.randomUUID(), at: Date.now(), label, detail },
    ...state.events,
  ].slice(0, MAX_EVENTS);
};

const today = () => new Date().toISOString().slice(0, 10);

const clock = (offsetMin = 0) => {
  const d = new Date(Date.now() + offsetMin * 60000);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const notify = async (
  title: string,
  body: string,
  url: string,
  category: string,
  urgent = false,
) => {
  if (!userId) return;
  await (supabase.rpc as any)("create_self_notification", {
    p_title: title,
    p_body: body,
    p_url: url,
    p_category: category,
    p_urgent: urgent,
  });
};


const WALKIN_TYPES = ["Consultation", "Follow-up", "Report Check"];
const AMBIENT = [
  { title: "Lab report received", body: "A new report was uploaded for review.", url: "/lab-orders", cat: "lab", urgent: false },
  { title: "Payment received", body: "৳800 collected via bKash at reception.", url: "/billing", cat: "billing", urgent: false },
  { title: "WhatsApp message", body: "A patient asked about today's serial.", url: "/communications", cat: "inbox", urgent: false },
  { title: "Follow-up due", body: "A chronic-care patient is due for review.", url: "/recalls", cat: "follow_up", urgent: false },
  { title: "Low stock alert", body: "Napa 500mg is below the reorder level.", url: "/inventory", cat: "inventory", urgent: true },
];

/* ------------------------------------------------------------------ */
/* WhatsApp / SMS conversation simulation                              */
/* ------------------------------------------------------------------ */

/** Opening messages a patient might send in on their own. */
const OPENERS = [
  "স্যার, আজকে কি সিরিয়াল পাওয়া যাবে?",
  "আসসালামু আলাইকুম, চেম্বার কয়টা পর্যন্ত খোলা?",
  "ওষুধ শেষ হয়ে গেছে, আবার লিখে দিবেন?",
  "Sir, report ready hoyeche. Kobe dekhabo?",
  "আমার প্রেসার আজ ১৫০/৯৫ এসেছে, কী করব?",
  "Can I take the medicine after food instead of before?",
  "বাচ্চার জ্বর ১০২, আজকে দেখানো যাবে?",
  "Visit fee koto sir? bKash a dite parbo?",
];

/** Follow-up lines a patient sends after the doctor replies. */
const REPLIES = [
  "জি আচ্ছা স্যার, ধন্যবাদ।",
  "ঠিক আছে, আমি চলে আসছি।",
  "Thank you sir, ami bikale asbo.",
  "আচ্ছা, ওষুধটা কি চালিয়ে যাব?",
  "Ok sir. Serial ta kivabe nibo?",
  "অনেক ধন্যবাদ, উপকার হলো।",
  "একটু পরে ফোন দিতে পারি স্যার?",
];

const CHANNELS: Array<"whatsapp" | "sms"> = ["whatsapp", "whatsapp", "whatsapp", "sms"];
const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

/** Threads we have already auto-replied to (keyed by the doctor's message id). */
const answered = new Set<string>();

/**
 * Injects inbound WhatsApp/SMS traffic. If the doctor has replied to a thread,
 * the patient answers back so reply flows can be tested end to end; otherwise a
 * new patient starts a fresh conversation.
 */
export async function simulateInboxActivity(): Promise<string | null> {
  if (!userId) return null;

  const { data: recent } = await supabase
    .from("inbox_messages")
    .select("id, patient_id, patient_name, phone, channel, direction, created_at")
    .order("created_at", { ascending: false })
    .limit(80);

  const rows = (recent || []) as any[];

  // Latest message per thread — a thread ending with an outgoing reply gets an answer.
  const seen = new Set<string>();
  const awaiting = rows.filter((m) => {
    const key = m.patient_id ? `id:${m.patient_id}` : `ph:${m.phone ?? "?"}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return m.direction === "out" && !answered.has(m.id);
  });

  if (awaiting.length) {
    const target = pick(awaiting);
    answered.add(target.id);
    const body = pick(REPLIES);
    await supabase.from("inbox_messages").insert({
      user_id: userId,
      patient_id: target.patient_id,
      patient_name: target.patient_name,
      phone: target.phone,
      channel: target.channel || "whatsapp",
      direction: "in",
      body,
      is_read: false,
    });
    await notify(
      `New ${target.channel === "sms" ? "SMS" : "WhatsApp"} reply`,
      `${target.patient_name || "Patient"}: ${body}`,
      "/communications",
      "inbox",
    );
    return `${target.patient_name || "Patient"} replied`;
  }

  // Otherwise a new patient starts a conversation.
  const { data: pts } = await supabase.from("patients").select("id, name, phone").limit(50);
  const patient: any = pick(pts || []);
  if (!patient) return null;

  const channel = pick(CHANNELS);
  const body = pick(OPENERS);
  await supabase.from("inbox_messages").insert({
    user_id: userId,
    patient_id: patient.id,
    patient_name: patient.name,
    phone: patient.phone,
    channel,
    direction: "in",
    body,
    is_read: false,
  });
  await notify(
    `New ${channel === "sms" ? "SMS" : "WhatsApp"} message`,
    `${patient.name}: ${body}`,
    "/communications",
    "inbox",
  );
  return `${patient.name} — ${channel === "sms" ? "SMS" : "WhatsApp"}`;
}



/** One simulation step: move the clinic day forward by a single action. */
async function tick() {
  if (!userId || busy) return;
  busy = true;
  try {
    const day = today();
    const { data, error } = await supabase
      .from("appointments")
      .select("id, serial_number, status, time, called_at, patients(name)")
      .eq("date", day)
      .order("serial_number", { ascending: true });
    if (error) throw error;

    const rows = (data || []) as any[];
    const active = rows.filter((r) => r.status !== "Cancelled");
    const inProgress = active.find((r) => r.status === "In Progress");
    const nextUp = active.find((r) => r.status === "Scheduled");
    const nameOf = (r: any) => r?.patients?.name || "Patient";

    if (inProgress) {
      // Finish the consultation that is running.
      await supabase
        .from("appointments")
        .update({ status: "Completed", completed_at: new Date().toISOString() })
        .eq("id", inProgress.id);
      await notify(
        "Consultation completed",
        `Serial ${inProgress.serial_number} — ${nameOf(inProgress)} has left the chamber.`,
        "/queue",
        "queue",
      );
      log("Completed", `Serial ${inProgress.serial_number} · ${nameOf(inProgress)}`);
    } else if (nextUp) {
      // Call the next patient in.
      const now = new Date().toISOString();
      await supabase
        .from("appointments")
        .update({ status: "In Progress", called_at: now, consult_started_at: now })
        .eq("id", nextUp.id);
      await notify(
        "Now serving",
        `Serial ${nextUp.serial_number} — ${nameOf(nextUp)} has been called in.`,
        "/queue",
        "queue",
        true,
      );
      log("Called in", `Serial ${nextUp.serial_number} · ${nameOf(nextUp)}`);
    } else {
      // Queue is empty: register a walk-in so the day keeps flowing.
      const { data: pts } = await supabase
        .from("patients")
        .select("id, name")
        .limit(50);
      const pool = (pts || []).filter(
        (p: any) => !rows.some((r: any) => r.patient_id === p.id),
      );
      const pick: any = (pool.length ? pool : pts || [])[
        Math.floor(Math.random() * Math.max(1, (pool.length ? pool : pts || []).length))
      ];
      if (!pick) throw new Error("No patients available to simulate a walk-in.");

      const { data: inserted, error: insErr } = await supabase
        .from("appointments")
        .insert({
          user_id: userId,
          patient_id: pick.id,
          date: day,
          time: clock(5),
          type: WALKIN_TYPES[Math.floor(Math.random() * WALKIN_TYPES.length)],
          status: "Scheduled",
          notes: "Walk-in registered at reception.",
        })
        .select("serial_number")
        .single();
      if (insErr) throw insErr;

      await notify(
        "New patient checked in",
        `Serial ${inserted?.serial_number ?? "-"} — ${pick.name} is waiting.`,
        "/reception-queue",
        "queue",
      );
      log("Checked in", `Serial ${inserted?.serial_number ?? "-"} · ${pick.name}`);
    }

    // Sprinkle an ambient clinic notification now and then.
    if (state.ticks % 3 === 2) {
      const a = AMBIENT[Math.floor(Math.random() * AMBIENT.length)];
      await notify(a.title, a.body, a.url, a.cat, a.urgent);
      log("Alert", a.title);
    }

    // Keep the WhatsApp/SMS inbox alive so replies can be tested.
    if (state.ticks % 2 === 0) {
      const msg = await simulateInboxActivity();
      if (msg) log("Message", msg);
    }


    state.ticks += 1;
    state.lastError = null;
  } catch (e: any) {
    state.lastError = e?.message ?? String(e);
    log("Error", state.lastError!);
  } finally {
    busy = false;
    onChange?.();
    emit();
  }
}

function schedule() {
  if (timer) clearInterval(timer);
  timer = setInterval(() => void tick(), state.intervalMs);
}

/** Wire the engine to the signed-in doctor and a refresh callback. */
export function configureSimulation(uid: string | null, refresh: () => void) {
  userId = uid;
  onChange = refresh;
  if (!uid) stopSimulation();
}

export function startSimulation() {
  if (!userId || state.running) return;
  state.running = true;
  window.localStorage.setItem(KEY_RUNNING, "true");
  log("Started", "Clinic day simulation is running.");
  emit();
  void tick();
  schedule();
}

export function stopSimulation() {
  if (timer) clearInterval(timer);
  timer = null;
  if (!state.running) return;
  state.running = false;
  window.localStorage.setItem(KEY_RUNNING, "false");
  log("Paused", "Simulation paused.");
  emit();
}

export function setSimulationSpeed(intervalMs: number) {
  state.intervalMs = intervalMs;
  window.localStorage.setItem(KEY_SPEED, String(intervalMs));
  if (state.running) schedule();
  emit();
}

/** Run a single step immediately without starting the timer. */
export function stepSimulation() {
  void tick();
}

/** Inject one inbound WhatsApp/SMS message (or a reply) on demand. */
export function injectDemoMessage() {
  void (async () => {
    try {
      const msg = await simulateInboxActivity();
      log("Message", msg ?? "No patients available to message.");
    } catch (e: any) {
      log("Error", e?.message ?? String(e));
    } finally {
      onChange?.();
      emit();
    }
  })();
}



export function shouldAutoStart() {
  return readBool(KEY_RUNNING, false);
}

export function getSimulationState() {
  return state;
}

export function subscribeSimulation(fn: Listener) {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}
